import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query and return whether it currently matches.
 *
 * Mobile-responsive pass (2026-05-30): the app had NO media-query hook —
 * layout widths were hard desktop pixels (260px tree, 580px rails, a 3-column
 * board that computes a negative width on a phone). This is the single source
 * of truth for "are we in a narrow / drawer layout" so the shell and the pages
 * agree on one breakpoint instead of each guessing with window.innerWidth.
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange(); // sync immediately in case the query changed between render + effect
    // Safari < 14 only has addListener/removeListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

/**
 * Breakpoint (px) below which the desktop 3-panel layout (left tree + content
 * + right rail) no longer fits and the app switches to the off-canvas drawer
 * + stacked layout. Matches Tailwind's `lg`. Benchmark device for the mobile
 * pass is the iPhone 16 Pro Max (440 × 956 CSS px), but the breakpoint covers
 * all phones and small tablets in portrait.
 */
export const MOBILE_BREAKPOINT = 1024;

/** True on phones + small tablets (viewport < lg). Drives drawer/stacked UI. */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
}
